import { useInfiniteScrollTable } from "@flaminem/ui-components";
import { sortBy } from "@flaminem/utils";
import { useNavigate } from "react-router";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions";
import { InfiniteScrollTable } from "src/components/Tables";
import { defaultRowHeight } from "src/config/table-config";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import { useSearchCustomersQuery } from "src/graphql/anti-fraud/queries/customers/SearchCustomersQuery";
import { useSearch } from "src/hooks/useSearch";
import { useTableURLParams } from "src/hooks/useTableURLParams";
import { useT } from "src/internationalization";
import { CustomerSummary } from "src/types/customer";

import { CustomersToolbar } from "./CustomersToolbar";
import { useGetColumns } from "./useGetColumns";

export default function CustomersTable() {
  const { pageSize, dimensions } = useDimensions();
  const t = useT();
  const navigate = useNavigate();

  const { offset, searchTerm, handleOnSearchTermChange } = useTableURLParams({}, {});

  const { searchLoading, filteredData, searchError, refetch } = useSearch({
    searchQueryHook: useSearchCustomersQuery,
    searchParams: {
      limit: pageSize,
      offset: offset,
      searchTerm: searchTerm,
    },
  });

  const sortFilteredData = filteredData?.customers
    ? sortBy(filteredData?.customers, "lastModification", "desc")
    : filteredData?.customers;

  const { data, totalCount } = useInfiniteScrollTable<CustomerSummary>(
    sortFilteredData ?? [],
    filteredData?.pageInfo?.totalCount ?? 0,
    filteredData?.pageInfo?.limit ?? pageSize,
    filteredData?.pageInfo?.offset ?? offset
  );

  const { customerListTableDefinitions, headlessWidgets } = useDynamicViewSchema();

  const columns = useGetColumns({
    customerListTable: customerListTableDefinitions,
    headlessWidgets,
  });

  const handleLoadMore = async (startIndex: number, stopIndex: number) => {
    if (!searchLoading) {
      refetch({
        limit: stopIndex - startIndex + 1,
        offset: startIndex,
        searchString: searchTerm && searchTerm?.length > 0 ? searchTerm : null,
      });
    }
  };

  return (
    <DimensionedLayout>
      {dimensions?.width && (
        <InfiniteScrollTable
          id="requests-table"
          title={t("page.customers.table.title")}
          className={"flex-1"}
          totalCount={totalCount}
          rowHeight={defaultRowHeight}
          fetchMore={handleLoadMore}
          scrollToIndex={totalCount && offset ? offset : -1}
          toolbar={() => (
            <CustomersToolbar
              searchTerm={searchTerm}
              handleOnSearchTermChange={handleOnSearchTermChange}
              placeholder={customerListTableDefinitions?.searchPlaceholder}
            />
          )}
          data={data ?? []}
          isLoading={searchLoading}
          columns={columns}
          pageSize={pageSize}
          errorText={searchError?.message}
          isError={searchError != null}
          onRowClick={(row) => navigate(`/customer/${row.data?.id}`)}
        />
      )}
    </DimensionedLayout>
  );
}
